'use strict'

import {useState, useEffect} from 'react'
import OutletCustomApi from '@app/custom-api/outlet-custom-api'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@app/hooks/use-multi-site'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useSiteType from '../hooks/use-site-type'

/**
 * Initializes and returns a custom Outlet API instance configured with the current site, organization, and locale parameters.
 * Utilizes hooks to retrieve the active site and locale, and fetches organization details from the application configuration.
 *
 * @returns {OutletCustomApi} An instance of OutletCustomApi initialized with siteId, organizationId, and locale.
 */
export const useInitializeOutletApi = () => {
    const {site, locale} = useMultiSite()
    const {app: appConfig} = getConfig()

    return new OutletCustomApi({
        parameters: {
            siteId: site.id,
            organizationId: appConfig.commerceAPI.parameters.organizationId,
            locale: locale.id
        }
    })
}

/**
 * Custom React hook to determine the campaign status for an outlet site.
 *
 * This hook checks if the current site is an outlet and, if so, fetches the campaign status
 * from a custom API using the current site and locale information. It manages loading state
 * and returns whether the campaign is active.
 *
 * @returns {{isCampaignActive: boolean|null, isLoading: boolean}} - An object containing:
 *   - isCampaignActive: Indicates if the campaign is active (true/false) or null if not yet loaded.
 *   - isLoading: Boolean indicating if the campaign status is currently being fetched.
 */
export const useOutletCampaignStatus = () => {
    const {isOutlet} = useSiteType()
    const OutletCustomApi = useInitializeOutletApi()
    const {site, locale} = useMultiSite()
    const {getTokenWhenReady} = useAccessToken()
    const [isCampaignActive, setIsCampaignActive] = useState(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        const fetchCampaignStatus = async () => {
            try {
                const accessToken = await getTokenWhenReady()
                const result = await OutletCustomApi.getCampaignStatus(
                    accessToken,
                    site?.id,
                    locale.id
                )
                setIsCampaignActive(result?.isCampaignActive)
            } catch (error) {
                console.error('Failed to fetch campaign status:', error)
            } finally {
                setIsLoading(false)
            }
        }

        if (isOutlet && isCampaignActive === null) {
            fetchCampaignStatus()
        } else {
            setIsLoading(false)
        }
    }, [])

    return {isCampaignActive, isLoading}
}

/**
 * Custom React hook that provides a function to fetch outlet configurations for the current site.
 *
 * Utilizes access token authentication and site context to retrieve outlet configuration data from the backend API.
 *
 * @returns {Function} getOutletConfigurations - An async function that fetches outlet configurations.
 *   @param {boolean} [includeCode=false] - Whether to include code details in the response.
 *   @returns {Promise<Object|null>} - Resolves with the outlet configurations object on success, or null on failure.
 */
export const useOutletConfigurations = () => {
    const {getTokenWhenReady} = useAccessToken()
    const {site} = useMultiSite()

    const getOutletConfigurations = async (includeCode = false) => {
        try {
            const accessToken = await getTokenWhenReady()

            const response = await fetch(
                `/api/outlet-configurations?siteId=${site?.id}&c_includeCode=${includeCode}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        authorization: `Bearer ${accessToken}`
                    }
                }
            )

            if (!response.ok) {
                throw new Error('Failed to fetch outlet configurations')
            }

            const result = await response.json()
            return result
        } catch (error) {
            console.error('Error fetching outlet configurations:', error)
            return null
        }
    }

    return getOutletConfigurations
}
